import en from "./en.json";
import es from "./es.json";
import _ from "lodash";
import mixpanel from "mixpanel-browser";
import { sortNatural } from "../../common.utils";
import workerSelectTemplate from "./worker_select.html";

/**
 * Payload emitted by the component when selection changes.
 * @typedef {Object} WorkerSelectUpdatePayload
 * @property {string|string[]} value Selected user SID in single mode or selected user SIDs in multi mode.
 */

/**
 * Bindings accepted by the workerSelect component.
 * @typedef {Object} WorkerSelectBindings
 * @property {string} [selectedWorker] Initial selected user SID when single mode is enabled.
 * @property {string[]} [selectedWorkers] Initial selected user SIDs when multi mode is enabled.
 * @property {(payload: WorkerSelectUpdatePayload) => void} onUpdate Callback invoked whenever selected users change.
 * @property {{sid: string}} grower Grower organization used to scope membership lookups.
 * @property {boolean} single Enables single-select mode when true.
 * @property {Object[]} workers Candidate growerMemberships available for selection.
 */

/**
 * Worker selector used across grower workflows.
 *
 * The component renders a searchable list of workers, supports single or multi-select behavior,
 * and emits selected user SID values through the onUpdate callback.
 */

export class WorkerSelectController {
    /* @ngInject */
    constructor(
        $scope,
        GrowerMembershipService,
        TimeTrackingVendorConfigurationService,
        $q,
    ) {
        this.$scope = $scope;
        this.GrowerMembershipService = GrowerMembershipService;
        this.TimeTrackingVendorConfigurationService =
            TimeTrackingVendorConfigurationService;
        this.$q = $q;
        this.selectedUsers = new Set();
        this.quickPickWorkers = [];
        this.showSpinner = {};
        this.sortBy = "first_name";
    }

    $onInit() {
        let ctrl = this;

        ctrl.$scope.$watchCollection(
            () => Array.from(ctrl.selectedUsers),
            (newValue, oldValue) => {
                ctrl.refreshWorkers().finally(() => {
                    ctrl.update();
                    ctrl.resetSearch();
                });
            },
        );

        ctrl.$scope.$watchCollection(
            () => ctrl.presetWorkers || ctrl.workers,
            () => {
                ctrl.addDefaultSearchResults();
                if (ctrl.searchQuery) {
                    ctrl.search();
                }
            },
        );

        ctrl.$scope.$watchCollection(
            () => ctrl.quickPickValues,
            () => {
                ctrl.refreshQuickPickWorkers();
            },
        );

        ctrl.$scope.$watch(
            () => ctrl.selectedWorker,
            () => {
                if (!ctrl.single) {
                    return;
                }

                ctrl.syncSelectionFromBindings();
            },
        );

        ctrl.$scope.$watchCollection(
            () => ctrl.selectedWorkers,
            () => {
                if (ctrl.single) {
                    return;
                }

                ctrl.syncSelectionFromBindings();
            },
        );

        ctrl.syncSelectionFromBindings();

        ctrl.detailed = Boolean(ctrl.detailed);

        // Paging state for results list (default chunk size 100)
        ctrl.resultsPageSize = 100;
        ctrl.currentResultsLimit = ctrl.resultsPageSize;
    }

    syncSelectionFromBindings() {
        let ctrl = this;
        let nextSelection = ctrl.single
            ? ctrl.selectedWorker
                ? [ctrl.selectedWorker]
                : []
            : ctrl.selectedWorkers || [];
        nextSelection = _.compact(nextSelection);

        let currentSelection = _.sortBy(Array.from(ctrl.selectedUsers));
        let normalizedNextSelection = _.sortBy(nextSelection);
        if (_.isEqual(currentSelection, normalizedNextSelection)) {
            return;
        }

        ctrl.selectedUsers = new Set(nextSelection);
    }

    search() {
        let ctrl = this;
        if (!ctrl.searchQuery) {
            ctrl.addDefaultSearchResults();
            return;
        }

        if (ctrl.presetWorkers) {
            const searchQuery = ctrl.searchQuery.toLowerCase();
            ctrl.results = ctrl.sortWorkers(
                ctrl.eligibleWorkers.filter((worker) =>
                    `${worker.first_name} ${worker.last_name} ${worker.employee_id || ""}`
                        .toLowerCase()
                        .includes(searchQuery),
                ),
            );
            return;
        }

        ctrl.GrowerMembershipService.search(
            ctrl.searchQuery,
            "grower_organization_sid:" + ctrl.grower.sid,
        ).then(({ hits }) => {
            ctrl.results = [];
            for (let hit of hits) {
                let worker = _.find(ctrl.eligibleWorkers, [
                    "sid",
                    hit.objectID,
                ]);
                if (worker) {
                    ctrl.results.push(worker);
                }
            }
            ctrl.results = ctrl.sortWorkers(ctrl.results);
        });
    }

    sortWorkers(workers) {
        let ctrl = this;
        let sortFields = {
            first_name: ["first_name", "last_name"],
            last_name: ["last_name", "first_name"],
        };

        if (ctrl.sortBy === "employee_id") {
            let workersWithEmployeeId = _.filter(
                workers,
                (worker) =>
                    !_.isNil(worker.employee_id) && worker.employee_id !== "",
            );
            let workersWithoutEmployeeId = _.filter(
                workers,
                (worker) =>
                    _.isNil(worker.employee_id) || worker.employee_id === "",
            );

            return [
                ...sortNatural(workersWithEmployeeId, "employee_id"),
                ..._.orderBy(
                    workersWithoutEmployeeId,
                    ["first_name", "last_name"],
                    ["asc", "asc"],
                ),
            ];
        }

        return _.orderBy(
            workers || [],
            sortFields[ctrl.sortBy] || sortFields.first_name,
            ["asc", "asc"],
        );
    }

    updateSortOrder() {
        let ctrl = this;
        ctrl.selectedWorkersDisplay = ctrl.sortWorkers(
            ctrl.selectedWorkersDisplay,
        );
        ctrl.quickPickWorkers = ctrl.sortWorkers(ctrl.quickPickWorkers);

        if (ctrl.searchQuery) {
            ctrl.results = ctrl.sortWorkers(ctrl.results);
            return;
        }

        ctrl.addDefaultSearchResults();
    }

    resetSearch() {
        let ctrl = this;
        ctrl.searchQuery = "";
        // Reset paging when clearing search
        ctrl.currentResultsLimit = ctrl.resultsPageSize;
        ctrl.addDefaultSearchResults();
        ctrl.showSpinner = {};
    }

    addDefaultSearchResults() {
        let ctrl = this;
        ctrl.eligibleWorkers = _.filter(
            ctrl.presetWorkers || ctrl.workers || [],
            (growerMembership) => {
                const userSid = ctrl.getWorkerUserSid(growerMembership);
                return userSid && !ctrl.selectedUsers.has(userSid);
            },
        );
        const ordered = ctrl.sortWorkers(ctrl.eligibleWorkers);
        ctrl.results = _.take(ordered, ctrl.currentResultsLimit);
        ctrl.refreshQuickPickWorkers();
    }

    refreshQuickPickWorkers() {
        let ctrl = this;
        let quickPickUserSids = _.uniq(_.compact(ctrl.quickPickValues || []));
        if (!quickPickUserSids.length) {
            ctrl.quickPickWorkers = [];
            return;
        }

        let workersByUserSid = _.keyBy(
            ctrl.presetWorkers || ctrl.workers || [],
            function (worker) {
                return ctrl.getWorkerUserSid(worker);
            },
        );

        ctrl.quickPickWorkers = ctrl.sortWorkers(
            _.filter(
                _.compact(
                    _.map(quickPickUserSids, function (quickPickUserSid) {
                        return workersByUserSid[quickPickUserSid];
                    }),
                ),
                function (worker) {
                    return !ctrl.selectedUsers.has(
                        ctrl.getWorkerUserSid(worker),
                    );
                },
            ),
        );
    }

    getQuickPickLabelKey() {
        let ctrl = this;
        return ctrl.quickPickLabelKey || "workerSelect.QUICK_PICK_LABEL";
    }

    refreshWorkers() {
        let ctrl = this;

        if (ctrl.selectedUsers.size === 0) {
            ctrl.selectedWorkersDisplay = [];
            return ctrl.$q.resolve();
        }

        if (ctrl.presetWorkers) {
            ctrl.selectedWorkersDisplay = ctrl.sortWorkers(
                ctrl.presetWorkers.filter((worker) =>
                    ctrl.selectedUsers.has(ctrl.getWorkerUserSid(worker)),
                ),
            );
            return ctrl.$q.resolve();
        }

        const userSids = Array.from(ctrl.selectedUsers).join(",");

        let growerMemberships = ctrl.GrowerMembershipService.list({
            user__sid__in: userSids,
            grower_organization__sid: ctrl.grower.sid,
        });

        return growerMemberships.then(function (memberships) {
            ctrl.selectedWorkersDisplay = ctrl.sortWorkers(memberships);
        });
    }

    getWorkerUserSid(worker) {
        if (!worker) {
            return undefined;
        }

        return worker.user;
    }

    removeWorker(userSid) {
        let ctrl = this;
        ctrl.selectedUsers.delete(userSid);
    }

    selectWorker(worker) {
        let ctrl = this;
        const userSid = ctrl.getWorkerUserSid(worker);

        if ((!ctrl.single || ctrl.selectedUsers.size === 0) && userSid) {
            ctrl.showSpinner[userSid] = true;
            ctrl.selectedUsers.add(userSid);
        }
    }

    update() {
        let ctrl = this;
        let value = [...ctrl.selectedUsers];
        if (ctrl.single) {
            value = value[0];
        }
        if (value || ctrl.presetWorkers) {
            ctrl.onUpdate({ value });
        }
    }

    openBadgeScanner() {
        let ctrl = this;
        ctrl.scanError = null;
        ctrl.lastScannedWorker = null;
        ctrl.scanning = true;
    }

    closeBadgeScanner() {
        let ctrl = this;
        ctrl.scanning = false;
        ctrl.processingScan = false;
    }

    handleBadgeScan(value) {
        let ctrl = this;
        ctrl.processingScan = true;
        ctrl.scanError = null;
        ctrl.lastScannedWorker = null;
        ctrl.TimeTrackingVendorConfigurationService.find_grower_membership(
            value,
            ctrl.grower.sid,
        )
            .then(
                function (growerMembership) {
                    let worker = _.find(ctrl.presetWorkers || ctrl.workers, [
                        "user",
                        growerMembership.user,
                    ]);
                    if (!worker) {
                        ctrl.scanError = "workerSelect.SCAN_NOT_ELIGIBLE";
                        return;
                    }
                    if (ctrl.selectedUsers.has(worker.user)) {
                        ctrl.scanError = "workerSelect.SCAN_ALREADY_SELECTED";
                        return;
                    }
                    ctrl.selectWorker(worker);
                    mixpanel.track("Selected worker by badge scan", {
                        company_sid: ctrl.grower.sid,
                    });
                    if (ctrl.single) {
                        ctrl.closeBadgeScanner();
                        return;
                    }
                    ctrl.lastScannedWorker = worker;
                },
                function () {
                    ctrl.scanError = "workerSelect.SCAN_NOT_FOUND";
                },
            )
            .finally(function () {
                ctrl.processingScan = false;
            });
    }

    loadMore() {
        let ctrl = this;
        // Increase limit by page size and recompute results (only applies when not actively searching)
        ctrl.currentResultsLimit += ctrl.resultsPageSize;
        // Rebuild default results if no active search query
        if (!ctrl.searchQuery) {
            ctrl.addDefaultSearchResults();
        }
    }
}

export default {
    bindings: {
        selectedWorker: "<",
        selectedWorkers: "<",
        quickPickValues: "<",
        quickPickLabelKey: "<",
        onUpdate: "&",
        grower: "<",
        single: "<",
        workers: "<",
        presetWorkers: "<?",
        searchInputId: "@?",
        detailed: "<?", // TODO: implement detailed view for workers that includes ssn and birth date
    },
    controller: WorkerSelectController,
    template: workerSelectTemplate,
    name: "workerSelect",
    translations: { en, es },
};
